import { z } from 'zod';
import { AgentSchema, AgentConfigInputSchema, AgentMarkSchema } from './agent.types.js';
import { AxonEventSchema } from './event.types.js';
import { SessionIndexEntrySchema } from './session-index.types.js';
import { ResourceSizeSchema, UserContentBlocksSchema } from '@runloop/reflex-contract';
import { BlueprintArchitectureSchema } from './blueprint.types.js';

// --- Agent endpoints ---

/**
 * The agent-creation API keeps `repoSlug` / `repoBranch` as top-level
 * convenience fields for backward compatibility with existing clients
 * (CLI, integrations, MCP tool inputs). The server normalizes them into
 * a `git-repo` plugin attachment before persisting — see
 * `AgentRunnerService.startAgent`.
 */
export const CreateAgentRequestSchema = AgentConfigInputSchema.extend({
  repoSlug: z.string().optional(),
  /** Branch to check out. Omit to use the repository's default branch, resolved at setup time. */
  repoBranch: z.string().optional(),
  /**
   * Who in the organization this session starts out shared with. Omit to
   * take the org's `defaultSessionShare`; send a value to override it for
   * this launch, including `none` to start private under an org that
   * shares by default. Whatever is chosen is applied as an ordinary
   * org-subject resource grant and is still subject to the org's sharing
   * ceiling, so a level the org forbids is skipped rather than honoured.
   */
  orgShare: z.enum(['none', 'view', 'edit']).optional(),
});
export type CreateAgentRequest = z.infer<typeof CreateAgentRequestSchema>;

/**
 * The launch response is the created agent plus any non-fatal launch warnings.
 * `launchWarnings` is present only when the launch had to adjust the requested
 * config to succeed — e.g. the requested branch no longer existed, so the run
 * fell back to the repository's default branch. The web launch form surfaces
 * each entry as a toast; a clean launch omits the field entirely.
 */
export const CreateAgentResponseSchema = AgentSchema.extend({
  launchWarnings: z.array(z.string()).optional(),
}).meta({ id: 'CreateAgentResponse' });
export type CreateAgentResponse = z.infer<typeof CreateAgentResponseSchema>;

/**
 * Fields a client may change on an existing agent. The display name flows
 * through the inline-edit affordance on the agent detail page; `pinned` is
 * driven by the sidebar's Pin/Unpin action. Add new keys here as more
 * in-place edits become supported (e.g. tags, color label).
 */
export const UpdateAgentRequestSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  pinned: z.boolean().optional(),
  /** Turning listeners off cancels every armed and pending listener. */
  listenersEnabled: z.boolean().optional(),
});
export type UpdateAgentRequest = z.infer<typeof UpdateAgentRequestSchema>;

/**
 * Apply or clear an agent's triage mark (`POST /agents/:id/mark`). A null
 * `mark` clears it. Kept out of {@link UpdateAgentRequestSchema} because the
 * server owns the mark's lifetime — it drops the value on the next turn start
 * or archive — so this is an action against the run, not a patch of a
 * client-owned column.
 */
export const SetAgentMarkRequestSchema = z.object({
  mark: AgentMarkSchema.nullable(),
});
export type SetAgentMarkRequest = z.infer<typeof SetAgentMarkRequestSchema>;

export const SetAgentMarkResponseSchema = AgentSchema;
export type SetAgentMarkResponse = z.infer<typeof SetAgentMarkResponseSchema>;

/**
 * Switch the model-provider subscription a *running* agent authenticates with
 * (`PUT /agents/:id/subscription`). `providerSecretId` is an `mps_*` id of a
 * `type='subscription'` model-provider secret the caller can access. The new
 * subscription must belong to the same provider family the agent already runs
 * on (a `claude-code` agent → a Claude Max subscription; a `codex` agent → a
 * Codex subscription) — the on-box CLI can't change mid-run. The swap is
 * applied on the agent's next turn/resume: the server re-points the agent at
 * the new secret and rewrites the on-box credential file (immediately when the
 * devbox is running, otherwise on the next resume). This is deliberately a
 * dedicated action rather than a field on {@link UpdateAgentRequestSchema},
 * because it re-resolves and pushes a credential rather than patching a column.
 */
export const SwitchAgentSubscriptionRequestSchema = z.object({
  providerSecretId: z.string().min(1),
});
export type SwitchAgentSubscriptionRequest = z.infer<typeof SwitchAgentSubscriptionRequestSchema>;

export const SwitchAgentSubscriptionResponseSchema = AgentSchema;
export type SwitchAgentSubscriptionResponse = z.infer<typeof SwitchAgentSubscriptionResponseSchema>;

/**
 * Sticky settings to switch on a running agent
 * (`POST /agents/:id/runtime-settings`). A patch so later settings can join
 * `model`. An empty patch is rejected.
 */
export const AgentRuntimeSettingsPatchSchema = z
  .object({
    /** A model id from the agent type's catalog, checked as a launch with it would be. */
    model: z.string().min(1).optional(),
  })
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: 'Provide at least one setting to change',
  });
export type AgentRuntimeSettingsPatch = z.infer<typeof AgentRuntimeSettingsPatchSchema>;

export const AgentRuntimeSettingsResponseSchema = AgentSchema;
export type AgentRuntimeSettingsResponse = z.infer<typeof AgentRuntimeSettingsResponseSchema>;

/**
 * Attach MCP servers to a *running* agent (`POST /agents/:id/mcp-servers`).
 * `mcpServerIds` are `mcp_*` config ids from the org's MCP registry, holding
 * only the servers to add — already-attached ids are ignored. The server
 * mints live MCP hub tokens for the agent's devbox and registers each server
 * in the agent's native config immediately, then records the ids on the
 * agent's `mcp-servers` attachment. Requires the devbox to be running (409
 * `agent_devbox_not_running` otherwise); a registration the box reports as
 * failed is a 502 (`mcp_registration_failed`) with nothing persisted.
 * Resolution is best-effort per id: unknown or disabled ids are dropped and
 * only the ids that resolved land on the attachment (compare the returned
 * agent's `attachments` to detect a partial drop) — the request 404s
 * (`mcp_config_not_found`) only when none resolve. Local (stdio) servers
 * are rejected outright. The response's `activation` says whether the
 * already-running agent process loaded the servers or whether they wait on
 * disk for its next start. Like the subscription switch, this is an action
 * against the run — it pushes state onto the box — not a patch of a
 * client-owned column.
 */
export const AttachAgentMcpServersRequestSchema = z.object({
  mcpServerIds: z.array(z.string().min(1)).min(1),
});
export type AttachAgentMcpServersRequest = z.infer<typeof AttachAgentMcpServersRequestSchema>;

/**
 * Why an attached tool server is not yet usable by the agent process that is
 * running right now. Registration always lands in the agent's native config
 * on disk, so every deferral resolves itself the next time that process
 * starts (a devbox resume re-runs the launch commands).
 *
 * - `restart-required`: the agent's CLI reads its MCP config only at startup
 *   and offers no way to add a server mid-session.
 * - `agent-unreachable`: the CLI does support live registration but did not
 *   take this one — no live connection, no answer in time, or it could not
 *   connect to the server.
 */
export const McpAttachDeferralReasonSchema = z.enum(['restart-required', 'agent-unreachable']);
export type McpAttachDeferralReason = z.infer<typeof McpAttachDeferralReasonSchema>;

/**
 * Whether a live attach reached the running agent process. `active` means
 * the agent can call the servers' tools on its very next turn; `deferred`
 * means they are registered but only the agent's next process will load
 * them. Both are successful attaches — the difference is what the user is
 * told to expect, never an error.
 */
export const McpAttachActivationSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('active'), serverNames: z.array(z.string()) }),
  z.object({ state: z.literal('deferred'), reason: McpAttachDeferralReasonSchema }),
]);
export type McpAttachActivation = z.infer<typeof McpAttachActivationSchema>;

export const AttachAgentMcpServersResponseSchema = z.object({
  agent: AgentSchema,
  activation: McpAttachActivationSchema,
});
export type AttachAgentMcpServersResponse = z.infer<typeof AttachAgentMcpServersResponseSchema>;

export const ListAgentsResponseSchema = z.array(AgentSchema);
export type ListAgentsResponse = z.infer<typeof ListAgentsResponseSchema>;

export const PaginatedAgentsResponseSchema = z.object({
  agents: z.array(AgentSchema),
  nextCursor: z.string().nullable(),
});
export type PaginatedAgentsResponse = z.infer<typeof PaginatedAgentsResponseSchema>;

export const GetAgentResponseSchema = AgentSchema;
export type GetAgentResponse = z.infer<typeof GetAgentResponseSchema>;

/**
 * Live devbox status poll (`GET /agents/:id/devbox-status`). The poll already
 * hits Runloop, which knows the box's real state, so it also carries the live
 * resource size read straight from the devbox launch parameters.
 *
 * `status` is the raw Runloop status string (`running`, `suspended`, …), `null`
 * when the agent has no devbox yet, or `'unknown'` when the lookup couldn't
 * resolve. `resourceSize` is the live size, or `null` when the box has no
 * devbox, reports a size outside our enum, or the lookup failed. UI surfaces
 * prefer this live size over the agent's persisted `sandboxOptions.resourceSize`
 * because it reflects the box as it actually runs. `architecture` is the CPU
 * architecture the box was launched with (`arm64` / `x86_64`), or `null` when
 * unknown — a box launched without an explicit architecture never reports one.
 */
export const DevboxStatusResponseSchema = z.object({
  status: z.string().nullable(),
  resourceSize: ResourceSizeSchema.nullable(),
  architecture: BlueprintArchitectureSchema.nullable(),
});
export type DevboxStatusResponse = z.infer<typeof DevboxStatusResponseSchema>;

export const StopAgentResponseSchema = AgentSchema;
export type StopAgentResponse = z.infer<typeof StopAgentResponseSchema>;

export const KillAgentResponseSchema = AgentSchema;
export type KillAgentResponse = z.infer<typeof KillAgentResponseSchema>;

export const ArchiveAgentResponseSchema = AgentSchema;
export type ArchiveAgentResponse = z.infer<typeof ArchiveAgentResponseSchema>;

/**
 * Bulk-archive request (`POST /agents/archive-all`). The archive is always
 * scoped to the caller's active org and their owner visibility; the optional
 * fields narrow it further. The sidebar's per-time-bucket "Archive all" sends
 * the bucket's `createdAt` bounds so it archives every matching run on the
 * server — not just the page the client has scrolled into view — and forwards
 * the active `userId` owner filter when one is set. Omitting every field
 * archives all of the org's non-archived runs the caller can see.
 */
export const ArchiveAllAgentsRequestSchema = z.object({
  /** Inclusive lower bound on `createdAt` (epoch ms). */
  createdAtFrom: z.number().int().optional(),
  /** Exclusive upper bound on `createdAt` (epoch ms). */
  createdAtTo: z.number().int().optional(),
  userId: z.string().optional(),
});
export type ArchiveAllAgentsRequest = z.infer<typeof ArchiveAllAgentsRequestSchema>;

export const ArchiveAllAgentsResponseSchema = z.object({
  /** Number of agents transitioned from active to archived by this call. */
  archived: z.number().int(),
});
export type ArchiveAllAgentsResponse = z.infer<typeof ArchiveAllAgentsResponseSchema>;

// --- Message endpoints ---

export const SendMessageRequestSchema = z.object({
  message: z.string().optional(),
  content: UserContentBlocksSchema.optional(),
});
export type SendMessageRequest = z.infer<typeof SendMessageRequestSchema>;

export const SendMessageResponseSchema = AxonEventSchema;
export type SendMessageResponse = z.infer<typeof SendMessageResponseSchema>;

/**
 * 202 body for the agent-command verbs (message, interrupt, control
 * response) under asynchronous (mailbox) command delivery: the command is
 * accepted and durable, but its send outcome was still unknown at the
 * server's bounded wait. `commandId` addresses the enqueued command.
 */
export const AgentCommandPendingResponseSchema = z
  .object({
    status: z.literal('pending'),
    commandId: z.string(),
  })
  .meta({ id: 'AgentCommandPendingResponse' });
export type AgentCommandPendingResponse = z.infer<typeof AgentCommandPendingResponseSchema>;

/**
 * Lifecycle status of one mailbox command, read back by the client that
 * issued it: `pending` (enqueued, no owner has picked it up), `claimed` (an
 * owner is delivering it — for a sleeping agent this spans the devbox wake
 * and connection rebuild), `sent` (the agent accepted it), or `failed`
 * (terminal; `failure` carries the stable code and optional detail).
 */
export const AgentCommandStatusSchema = z.enum(['pending', 'claimed', 'sent', 'failed']);
export type AgentCommandStatus = z.infer<typeof AgentCommandStatusSchema>;

export const AgentCommandFailureResponseSchema = z
  .object({
    code: z.string(),
    detail: z.string().optional(),
    statusCode: z.number().optional(),
  })
  .meta({ id: 'AgentCommandFailureResponse' });
export type AgentCommandFailureResponse = z.infer<typeof AgentCommandFailureResponseSchema>;

export const AgentCommandStatusResponseSchema = z
  .object({
    commandId: z.string(),
    status: AgentCommandStatusSchema,
    failure: AgentCommandFailureResponseSchema.optional(),
  })
  .meta({ id: 'AgentCommandStatusResponse' });
export type AgentCommandStatusResponse = z.infer<typeof AgentCommandStatusResponseSchema>;

/**
 * Upper bound on the server-side wait a command-status read may request.
 * Long enough that a client watching a sleeping agent's send makes a
 * handful of requests over a minute rather than one per second; short
 * enough to stay well inside proxy idle timeouts.
 */
export const AGENT_COMMAND_STATUS_MAX_WAIT_MS = 25_000;

export const AgentCommandStatusQuerySchema = z.object({
  /**
   * Milliseconds to hold the request open waiting for a terminal status
   * (`sent` / `failed`). Omit or `0` for an immediate read. Capped at
   * `AGENT_COMMAND_STATUS_MAX_WAIT_MS`.
   */
  wait: z.coerce.number().int().min(0).max(AGENT_COMMAND_STATUS_MAX_WAIT_MS).optional(),
});
export type AgentCommandStatusQuery = z.infer<typeof AgentCommandStatusQuerySchema>;

// --- Control response endpoints ---

/** Provider control sent through the owned agent command mailbox. */
export const CancelBackgroundTaskControlSchema = z.object({
  action: z.literal('cancel_background_task'),
  taskId: z.string().min(1),
});
export type CancelBackgroundTaskControl = z.infer<typeof CancelBackgroundTaskControlSchema>;

export const ControlResponseRequestSchema = z.object({
  payload: z.unknown().refine((val) => val !== undefined && val !== null, {
    message: 'payload is required',
  }),
});
export type ControlResponseRequest = z.infer<typeof ControlResponseRequestSchema>;

/** 200 body for a control response the agent's provider accepted. */
export const ControlResponseOkResponseSchema = z.object({ ok: z.literal(true) });
export type ControlResponseOkResponse = z.infer<typeof ControlResponseOkResponseSchema>;

// --- Stream endpoints ---

export const AgentStreamResponseSchema = z.array(AxonEventSchema);
export type AgentStreamResponse = z.infer<typeof AgentStreamResponseSchema>;

/**
 * One window of an agent's event history, computed by the server's session
 * page resolver over `agents_session_index`. Sequences are inclusive.
 * `prevCursor` is the `before` value that fetches the previous page (null
 * when `hasMore` is false); `hasMore` compares `startSeq` against the
 * agent's index coverage watermark.
 */
export const AgentStreamPageInfoSchema = z
  .object({
    startSeq: z.number(),
    endSeq: z.number(),
    prevCursor: z.number().nullable(),
    hasMore: z.boolean(),
  })
  .meta({ id: 'AgentStreamPageInfo' });
export type AgentStreamPageInfo = z.infer<typeof AgentStreamPageInfoSchema>;

/**
 * Paged stream response for agents with a session index. Explicitly
 * opted into: only requests carrying `paged=true` or a `before` cursor
 * receive the envelope, and only for indexed agents — every other
 * request (legacy clients included) gets the bare event array
 * ({@link AgentStreamResponseSchema}).
 */
export const PagedAgentStreamResponseSchema = z
  .object({
    events: z.array(AxonEventSchema),
    page: AgentStreamPageInfoSchema,
  })
  .meta({ id: 'PagedAgentStreamResponse' });
export type PagedAgentStreamResponse = z.infer<typeof PagedAgentStreamResponseSchema>;

export const AgentStreamQuerySchema = z.object({
  /**
   * Opt into the paged `{ events, page }` envelope: the initial
   * turn-aligned window instead of the full history. Ignored (legacy
   * full array) for agents without a session index.
   */
  paged: z.enum(['true', 'false']).optional(),
  /**
   * Page-back cursor: the window strictly before this sequence, in the
   * paged envelope. Implies `paged`.
   */
  before: z.coerce.number().int().positive().optional(),
  /**
   * With `toSeq`: the exact inclusive sequence window `[fromSeq, toSeq]`,
   * in the paged envelope. The turn-precise continuous-scroll prefetch —
   * the client computes a turn's bounds from adjacent session-index rows
   * and asks for exactly that slice. Ignored without `toSeq`, and `before`
   * wins when both are sent. Indexed agents only, same rollout gate as
   * `paged`: legacy agents and a disabled gate answer with the full array.
   * Nonnegative rather than positive: the coverage watermark floor is 0
   * ("before the first event"), and the final preamble range starts there.
   */
  fromSeq: z.coerce.number().int().nonnegative().optional(),
  /** See `fromSeq`; ignored without it. */
  toSeq: z.coerce.number().int().nonnegative().optional(),
});
export type AgentStreamQuery = z.infer<typeof AgentStreamQuerySchema>;

/**
 * One `agents_session_index` row on the wire: the type-discriminated entry
 * plus its Axon anchor (`seqNumber`) and event time (epoch ms, null when
 * the anchoring event carried none).
 */
export const AgentSessionIndexEntrySchema = z
  .object({
    seqNumber: z.number(),
    timestamp: z.number().nullable(),
    entry: SessionIndexEntrySchema,
  })
  .meta({ id: 'AgentSessionIndexEntry' });
export type AgentSessionIndexEntry = z.infer<typeof AgentSessionIndexEntrySchema>;

/**
 * The whole session index for one agent — what the fast-nav rail renders
 * before any events load. `sessionIndexFromSeq` is the coverage watermark
 * (rows are complete for sequences at or above it; null means the agent
 * has no index and serves full history).
 */
export const AgentSessionIndexResponseSchema = z
  .object({
    entries: z.array(AgentSessionIndexEntrySchema),
    sessionIndexFromSeq: z.number().nullable(),
  })
  .meta({ id: 'AgentSessionIndexResponse' });
export type AgentSessionIndexResponse = z.infer<typeof AgentSessionIndexResponseSchema>;

// --- Server status ---

/**
 * Public `/status` response. Only carries the deployed app version — secret
 * presence and operational config (`runloopApiKey`, `githubWebhookSecret`,
 * `runloopBaseUrl`, `slackConfigured`) moved to {@link AdminServerStatusSchema}
 * behind `admin:read` in audit M4. The web client merges in the admin
 * fields with a separate fetch when the caller is a super-admin.
 */
export const ServerStatusSchema = z.object({
  version: z.string(),
  /** Git commit SHA of the running build, when the host bakes it in. */
  commit: z.string().optional(),
});
export type ServerStatus = z.infer<typeof ServerStatusSchema>;

export const AdminServerStatusSchema = z.object({
  version: z.string(),
  runloopApiKey: z.boolean(),
  runloopBaseUrl: z.string(),
  githubWebhookSecret: z.boolean(),
  slackConfigured: z.boolean().optional(),
});
export type AdminServerStatus = z.infer<typeof AdminServerStatusSchema>;

// --- GitHub repo listing ---

export const GitHubRepoSchema = z.object({
  fullName: z.string(),
  name: z.string(),
  owner: z.string(),
  defaultBranch: z.string(),
  private: z.boolean(),
  description: z.string().nullable(),
  updatedAt: z.string(),
  language: z.string().nullable(),
});
export type GitHubRepo = z.infer<typeof GitHubRepoSchema>;

export const GitHubRepoListResponseSchema = z.object({
  repos: z.array(GitHubRepoSchema),
  hasToken: z.boolean().optional(),
  hasInstallation: z.boolean().optional(),
  /**
   * `true` when `repos` is the caller's ENTIRE accessible repo set, so the
   * client can answer every subsequent search by filtering this list locally
   * instead of asking the server per keystroke.
   *
   * `false` means the response is a partial view — the first page, served
   * while the rest of the caller's repos page out of GitHub in the background,
   * or a list trimmed by `per_page`. Clients should keep searching
   * server-side (and refetch the list) until it flips to `true`.
   */
  complete: z.boolean().optional(),
});
export type GitHubRepoListResponse = z.infer<typeof GitHubRepoListResponseSchema>;

// --- GitHub branches ---

export const GitHubBranchSchema = z.object({
  name: z.string(),
});
export type GitHubBranch = z.infer<typeof GitHubBranchSchema>;

export const GitHubBranchListResponseSchema = z.object({
  branches: z.array(GitHubBranchSchema),
});
export type GitHubBranchListResponse = z.infer<typeof GitHubBranchListResponseSchema>;

// --- Profile stats (agent runs grid, top repos) ---

export const ProfileStatsResponseSchema = z
  .object({
    /**
     * Agent run starts per UTC `YYYY-MM-DD` day within the year window.
     * Each value is the number of agents the user created on that day,
     * scoped to the active organization. Used by the contribution heatmap
     * to render per-day activity.
     */
    agentRunStartsByDay: z.record(z.string(), z.number()),
    topRepositories: z.array(
      z.object({
        repoSlug: z.string(),
        count: z.number(),
      }),
    ),
    /**
     * Total agents created by the user within the year window — including
     * agents not tied to a repository (which never reach `topRepositories`).
     * Lets the Top Repositories card surface the no-repo remainder so the
     * sum of visible repo runs ≠ heatmap total isn't read as a bug.
     */
    totalRuns: z.number().int().nonnegative(),
    /**
     * Number of `totalRuns` that carry a Git repository attachment — i.e.
     * the sum of `topRepositories[*].count` if no truncation applied (the
     * server caps the list at 10 repos). Exposed separately so the UI can
     * differentiate "10 of 25 repos" from "all repos accounted for".
     */
    repoTaggedRuns: z.number().int().nonnegative(),
  })
  .meta({ id: 'ProfileStatsResponse' });
export type ProfileStatsResponse = z.infer<typeof ProfileStatsResponseSchema>;

// --- Webhook activity ---

export const WebhookEventRecordSchema = z.object({
  source: z.string(),
  path: z.string(),
  receivedAt: z.number(),
  durationMs: z.number(),
  statusCode: z.number(),
  eventType: z.string().optional(),
  error: z.string().optional(),
});
export type WebhookEventRecord = z.infer<typeof WebhookEventRecordSchema>;

export const WebhookSourceSummarySchema = z.object({
  source: z.string(),
  lastReceivedAt: z.number().nullable(),
  totalReceived: z.number(),
  totalErrors: z.number(),
  lastError: z.object({ message: z.string(), at: z.number() }).optional(),
  recentEvents: z.array(WebhookEventRecordSchema),
});
export type WebhookSourceSummary = z.infer<typeof WebhookSourceSummarySchema>;

export const WebhookActivityResponseSchema = z.object({
  serverStartedAt: z.number(),
  sources: z.array(WebhookSourceSummarySchema),
});
export type WebhookActivityResponse = z.infer<typeof WebhookActivityResponseSchema>;

// --- Error responses ---

export const ApiErrorSchema = z.object({
  error: z.string(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

/** Irreversible, platform-admin-only account erasure. No free-text PII in audit reasons. */
export const EraseUserRequestSchema = z
  .object({
    confirmUserId: z.string().min(1),
    reason: z.enum(['privacy_request', 'retention_policy', 'test_account']),
  })
  .strict();
export type EraseUserRequest = z.infer<typeof EraseUserRequestSchema>;
export const UserErasureResponseSchema = z.object({
  userId: z.string(),
  erasedAt: z.number(),
  followUp: z.enum(['required', 'reviewed']),
  secretMirrors: z.array(z.object({ organizationId: z.string(), secretId: z.string() })),
  reviewCompletedAt: z.number().nullable(),
});
export type UserErasureResponse = z.infer<typeof UserErasureResponseSchema>;
export const ReviewUserErasureRequestSchema = z
  .object({
    confirmUserId: z.string().min(1),
    externalCredentialsRevoked: z.literal(true),
    processorsAndContentReviewed: z.literal(true),
    backupRetentionApplied: z.literal(true),
  })
  .strict();
export type ReviewUserErasureRequest = z.infer<typeof ReviewUserErasureRequestSchema>;
